import { makeObservable, observable, action, computed, runInAction, toJS } from "mobx";
import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import audioProcessor from "../../audio-processor.js?raw";
import { debounce } from "lodash-es";
import { DateTime } from "luxon";
import {
  AssemblyAIMessage,
  TimestampedAssemblyAITurnMessage,
  TranscriptSnapshot,
} from "@/domains/dexie-db/audio-capture/types";
import { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import { api } from "@/modules/api";
import { normalizeTranscriptsToV3Map } from "@/store/audio-capture/transcription/utils/legacy";
import {
  closeAudioContext,
  closeWebSocket,
  disconnectAudioNode,
} from "@/store/audio-capture/audio-capture-error-handling-helpers";
import { objectModule } from "@/modules/object";
import type { TranscriptionChannelManager } from "@/store/audio-capture/transcription/interfaces";

interface AudioBufferItem {
  data: ArrayBuffer;
  timestamp: number;
}

interface TranscriptionDisconnection {
  ended: boolean;
}

export class TranscriptionManager implements TranscriptionChannelManager {
  // Buffering and reconnection constants
  readonly AUDIO_BUFFER_FLUSH_INTERVAL_MS = 50; // Flush buffered audio every 50ms
  readonly RECONNECTION_DELAY_MS = 1000; // 1 second delay between reconnection attempts
  readonly MAX_RECONNECTION_ATTEMPTS = Infinity; // Maximum number of reconnection attempts
  readonly WEBSOCKET_HEALTH_CHECK_INTERVAL_MS = 10 * 1000; // 10 seconds
  readonly WEBSOCKET_CONNECTION_TIMEOUT_MS = 5000; // 5 seconds
  readonly TOKEN_EXPIRATION_BUFFER_MS = 30 * 1000; // 30 seconds
  readonly TOKEN_CHECK_INTERVAL_MS = 10 * 1000; // 10 seconds
  readonly ASSEMBLY_AI_TOKEN_SESSION_DURATION_MS = 10800_000; // 3 hours
  readonly ASSEMBLY_AI_WS_URL = "wss://streaming.assemblyai.com/v3/ws";
  readonly MIN_AUDIO_CHUNKS = 7; // 7 * 8ms = 56ms minimum buffer
  readonly MAX_AUDIO_CHUNKS = 125; // 125 * 8ms = 1000ms maximum buffer
  readonly WEBSOCKET_HEALTH_TIMEOUT_MS = 30 * 1000; // 30 seconds without activity is unhealthy

  // Token Management
  assemblyAiToken: string | null = null;
  assemblyAiTokenExpiresAt: number | null = null;
  assemblyAiTokenSessionEndsAt: number | null = null;
  tokenCheckInterval: NodeJS.Timeout | null = null;
  isRefreshingToken: boolean = false;

  // WebSocket resilience and buffering
  audioBuffer: AudioBufferItem[] = [];
  audioBufferFlushInterval: NodeJS.Timeout | null = null;
  isReconnecting: boolean = false;
  reconnectionAttempts: number = 0;
  reconnectionTimeout: NodeJS.Timeout | null = null;
  isWebSocketHealthy: boolean = false;
  lastWebSocketActivity: number = 0;
  webSocketHealthCheckInterval: NodeJS.Timeout | null = null;
  wsConnection: WebSocket | null = null;
  isPaused: boolean = false;

  // AudioContext
  audioContext: AudioContext | null = null;
  audioSource: MediaStreamAudioSourceNode | null = null;
  audioWorkletNode: AudioWorkletNode | null = null;

  // Transcription
  transcriptMap: Map<number, TimestampedAssemblyAITurnMessage> = new Map();
  transcriptSnapshots: TranscriptSnapshot[] = [];
  transcriptRestoredOffset: number = 0;

  readonly channel: number;
  readonly audioCaptureStore: AppStoreAudioCaptureStore;

  constructor(audioCaptureStore: AppStoreAudioCaptureStore, channel: number) {
    this.audioCaptureStore = audioCaptureStore;
    this.channel = channel;

    makeObservable(this, {
      audioCaptureStore: false,
      channel: false,
      send: false,

      // Constants - don't need to be observable
      WEBSOCKET_HEALTH_CHECK_INTERVAL_MS: false,
      TOKEN_EXPIRATION_BUFFER_MS: false,
      TOKEN_CHECK_INTERVAL_MS: false,
      WEBSOCKET_CONNECTION_TIMEOUT_MS: false,
      WEBSOCKET_HEALTH_TIMEOUT_MS: false,
      ASSEMBLY_AI_WS_URL: false,
      AUDIO_BUFFER_FLUSH_INTERVAL_MS: false,
      MIN_AUDIO_CHUNKS: false,
      MAX_AUDIO_CHUNKS: false,
      RECONNECTION_DELAY_MS: false,
      MAX_RECONNECTION_ATTEMPTS: false,
      ASSEMBLY_AI_TOKEN_SESSION_DURATION_MS: false,

      // Observables
      assemblyAiToken: observable,
      assemblyAiTokenExpiresAt: observable,
      assemblyAiTokenSessionEndsAt: observable,
      tokenCheckInterval: observable,
      isRefreshingToken: observable,

      isReconnecting: observable,
      audioBuffer: observable,
      audioBufferFlushInterval: observable,

      wsConnection: observable,
      isWebSocketHealthy: observable,
      lastWebSocketActivity: observable,
      webSocketHealthCheckInterval: observable,
      reconnectionAttempts: observable,
      reconnectionTimeout: observable,
      isPaused: observable,

      audioContext: observable,
      audioSource: observable,
      audioWorkletNode: observable,

      transcriptMap: observable,
      transcriptSnapshots: observable,
      transcriptRestoredOffset: observable,

      // CORE
      initializeTranscription: action,
      setupTranscription: action,
      startTranscription: action,
      stopTranscription: action,
      pauseTranscription: action,
      resumeTranscription: action,
      restoreTranscripts: action,
      reset: action,

      // TRANSCRIPTION
      allTranscriptedParts: computed,
      transcript: computed,
      isTranscriptionActive: computed,
      isTranscriptionPaused: computed,
      _createTranscriptSnapshot: action,

      // WEBSOCKET CONNECTION
      connectionStatus: computed,
      initializeWebSocket: action,
      cleanupWebSocketAndAudio: action,
      _createWebSocketConnection: action,
      _attemptWebSocketReconnection: false,
      _attemptWebSocketReconnectionAfterDelay: false,
      _handleWebSocketDisconnection: action,
      _handleWebSocketMessage: action,

      // BUFFERING
      flushAudioBuffer: action,
      _bufferAudioData: action,
      _startAudioBufferFlush: action,
      _stopAudioBufferFlush: action,

      // TOKEN MONITORING
      _getAssemblyAiToken: action,
      _isTokenExpiredOrExpiringSoon: false,
      _isSessionExpiredOrExpiringSoon: false,
      _startTokenMonitoring: action,
      _stopTokenMonitoring: action,
      _refreshTokenAndReconnectWebSocket: action,

      // WEBSOCKET HEALTH CHECKS
      _checkWebSocketHealth: action,
      _startWebSocketHealthCheck: true,
      _stopWebSocketHealthCheck: action,
    });
  }

  /**
   * CORE
   */

  initializeTranscription({ clearTranscript = false }: { clearTranscript?: boolean } = {}) {
    logger.debug({
      message: "[TranscriptionManager] initializeTranscription",
      info: { clearTranscript, channel: this.channel },
    });
    if (clearTranscript) {
      this.transcriptMap.clear();
      this.transcriptSnapshots = [];
    }
    this.audioBuffer = [];
    this.reconnectionAttempts = 0;
    this.isReconnecting = false;
    this.isWebSocketHealthy = false;
    this.lastWebSocketActivity = 0;
    this.isPaused = false;
  }

  async setupTranscription(stream: MediaStream | null) {
    logger.debug({
      message: "[TranscriptionManager] setupTranscription",
      info: { stream: !!stream, channel: this.channel },
    });
    if (!stream) return;

    closeAudioContext(this.audioContext);

    // Reference set up from Assembly AI: https://github.com/AssemblyAI/realtime-transcription-browser-js-example/blob/master/public/index.js
    // Initialize AudioWorkletNode - This is for audio -> stream -> transcription
    this.audioContext = new AudioContext({
      sampleRate: this.audioCaptureStore.SAMPLE_RATE,
      latencyHint: "interactive",
      // latencyHint: "balanced",
    });

    this.audioSource = this.audioContext.createMediaStreamSource(stream);

    // Original: https://stackoverflow.com/a/52774181
    const processorBlob = new Blob([audioProcessor], { type: "text/javascript" });
    const processorURL = URL.createObjectURL(processorBlob);
    try {
      await this.audioContext.audioWorklet.addModule(processorURL);
    } finally {
      // Clean up the processor URL to prevent memory leaks
      URL.revokeObjectURL(processorURL);
    }

    this.audioWorkletNode = new AudioWorkletNode(this.audioContext, "audio-processor");
    this.audioSource.connect(this.audioWorkletNode);
    this.audioWorkletNode.connect(this.audioContext.destination);

    this.audioWorkletNode.port.onmessage = event => {
      this._bufferAudioData(event.data.audio_data);
    };
  }

  startTranscription() {
    this.isPaused = false;
    this._startTokenMonitoring();
    this._startAudioBufferFlush();
    this._startWebSocketHealthCheck();
  }

  stopTranscription() {
    this._stopTokenMonitoring();
    this._stopAudioBufferFlush();
    this._stopWebSocketHealthCheck();
    this.isPaused = false;
  }

  pauseTranscription() {
    if (this.isPaused) return;

    this.isPaused = true;

    this._stopAudioBufferFlush();
    this._stopWebSocketHealthCheck();

    const suspend = this.audioContext?.suspend?.();
    if (suspend) {
      void suspend.catch(() => undefined);
    }
  }

  resumeTranscription() {
    if (!this.isPaused) return;

    this.isPaused = false;

    const resume = this.audioContext?.resume?.();
    if (resume) {
      void resume.catch(() => undefined);
    }

    this._startAudioBufferFlush();
    this._startWebSocketHealthCheck();
    this.flushAudioBuffer();
  }

  reset({ clearTranscript }: { clearTranscript?: boolean } = {}) {
    this.isRefreshingToken = false;

    // Reset buffering and reconnection state
    this.audioBuffer = [];
    this.isReconnecting = false;
    this.reconnectionAttempts = 0;
    this.isWebSocketHealthy = false;
    this.lastWebSocketActivity = 0;
    this.isPaused = false;

    if (clearTranscript) {
      this.transcriptMap.clear();
      this.transcriptSnapshots = [];
      this.transcriptRestoredOffset = 0;
    }

    if (this.reconnectionTimeout) {
      clearTimeout(this.reconnectionTimeout);
      this.reconnectionTimeout = null;
    }

    this._stopTokenMonitoring();
    this._stopAudioBufferFlush();
    this._stopWebSocketHealthCheck();
  }

  /**
   * TRANSCRIPTION
   */

  get allTranscriptedParts() {
    const all: (TimestampedAssemblyAITurnMessage[] | TranscriptionDisconnection)[] = [];

    const disconnection: TranscriptionDisconnection = { ended: false };

    // Add all previous snapshots in order
    // NOTE: Transcripts are already normalized to V3 format when restored from DB
    this.transcriptSnapshots.forEach(snapshot => {
      const sortedMessages = Array.from(snapshot.transcriptMap.values()).sort(
        (a, b) => a.turn_order - b.turn_order
      );
      if (sortedMessages.length || sortedMessages.find(message => message.transcript.trim())) {
        all.push(sortedMessages, disconnection);
      }
    });

    // Add current transcript
    if (this.transcriptMap.size > 0) {
      const sortedMessages = Array.from(this.transcriptMap.values()).sort(
        (a, b) => a.turn_order - b.turn_order
      );
      if (sortedMessages.length || sortedMessages.find(message => message.transcript.trim())) {
        all.push(sortedMessages);
      }
    }

    if (this.reconnectionAttempts >= this.MAX_RECONNECTION_ATTEMPTS && !this.isReconnecting) {
      all.push({ ended: true });
    }

    return all;
  }

  get transcript(): string {
    const allTranscripts: string[] = this.allTranscriptedParts.map(part =>
      Array.isArray(part)
        ? part
            .map(message => message.transcript)
            .join(" ")
            .trim()
        : part.ended
          ? " ...[transcription ended]"
          : " ...[temporarily disconnected]... "
    );

    if (allTranscripts.length === 0) return "";

    return allTranscripts.join("").trim();
  }

  get isTranscriptionActive(): boolean {
    return (
      this.audioCaptureStore.status === RecordingStatus.RECORDING &&
      this.connectionStatus.status === "connected" &&
      this.isWebSocketHealthy
    );
  }

  get isTranscriptionPaused(): boolean {
    if (this.isPaused) {
      return true;
    }
    return (
      this.audioCaptureStore.status === RecordingStatus.RECORDING &&
      this.connectionStatus.status !== "connected" &&
      this.channel === 0
    );
  }

  restoreTranscripts({
    transcriptSnapshots,
    transcripts,
  }: {
    transcriptSnapshots?: TranscriptSnapshot[];
    transcripts?: AssemblyAIMessage[];
  }) {
    this.transcriptSnapshots = transcriptSnapshots || [];

    if (transcripts && transcripts.length > 0) {
      const v3TranscriptMap = normalizeTranscriptsToV3Map(transcripts);

      for (const [turnOrder, transcript] of v3TranscriptMap) {
        this.transcriptMap.set(turnOrder, transcript);
      }

      const maxTurnOrder = Math.max(...v3TranscriptMap.keys(), 0);
      this.transcriptRestoredOffset = maxTurnOrder + 1;
    }
  }

  _createTranscriptSnapshot(): void {
    if (this.transcriptMap.size === 0) return;

    const sortedMessages = Array.from(this.transcriptMap.values()).sort(
      (a, b) => a.turn_order - b.turn_order
    );

    const snapshot: TranscriptSnapshot = {
      // Remove proxies so we can serialize the snapshot.
      transcriptMap: new Map(toJS(this.transcriptMap)),
      snapshotTime: Date.now(),
    };

    runInAction(() => {
      this.transcriptSnapshots.push(snapshot);
      this.transcriptMap.clear();
      this.transcriptRestoredOffset = 0;
    });

    if (this.audioCaptureStore.activeNoteId) {
      void this.audioCaptureStore.persistence.appendTranscriptSnapshot({
        noteId: this.audioCaptureStore.activeNoteId,
        recordingId: this.audioCaptureStore.currentSession?.recordingId,
        snapshot,
        channel: this.channel,
      });
    }

    logger.debug({
      message: "[AudioCaptureTranscriptionStore] Created transcript snapshot",
      info: {
        transcriptSize:
          this.transcriptSnapshots[this.transcriptSnapshots.length - 1].transcriptMap.size,
        totalMessages: sortedMessages.length,
      },
    });
  }

  /**
   * WEBSOCKET CONNECTION
   */

  get connectionStatus(): { time: number; status: string } {
    if (!this.wsConnection)
      return { time: this.audioCaptureStore.heartbeatForComputedValues, status: "disconnected" };

    switch (this.wsConnection.readyState) {
      case WebSocket.CONNECTING:
        return { time: this.audioCaptureStore.heartbeatForComputedValues, status: "connecting" };
      case WebSocket.OPEN:
        return {
          time: this.audioCaptureStore.heartbeatForComputedValues,
          status: this.isWebSocketHealthy ? "connected" : "unhealthy",
        };
      case WebSocket.CLOSING:
        return { time: this.audioCaptureStore.heartbeatForComputedValues, status: "disconnecting" };
      case WebSocket.CLOSED:
        return { time: this.audioCaptureStore.heartbeatForComputedValues, status: "disconnected" };
      default:
        return { time: this.audioCaptureStore.heartbeatForComputedValues, status: "unknown" };
    }
  }

  async _createWebSocketConnection(): Promise<void> {
    if (!this.assemblyAiToken) {
      throw new Error("No valid token available for WebSocket connection");
    }

    logger.debug({
      message: "[TranscriptionManager] _createWebSocketConnection",
      info: { channel: this.channel, wsConnection: !!this.wsConnection },
    });

    const params = new URLSearchParams({
      sample_rate: this.audioCaptureStore.SAMPLE_RATE.toString(),
      token: this.assemblyAiToken,
      format_turns: "true",
    });

    return new Promise((resolve, reject) => {
      try {
        const wsConnection = new WebSocket(`${this.ASSEMBLY_AI_WS_URL}?${params.toString()}`);
        runInAction(() => (this.wsConnection = wsConnection));

        const connectionTimeout = setTimeout(() => {
          if (this.wsConnection?.readyState !== WebSocket.OPEN) {
            closeWebSocket(this.wsConnection);
            reject(new Error("WebSocket connection timeout"));
          }
        }, this.WEBSOCKET_CONNECTION_TIMEOUT_MS);

        wsConnection.onopen = () => {
          clearTimeout(connectionTimeout);
          logger.debug({
            message: "[AppStoreAudioCaptureStore] WebSocket Connected",
          });

          runInAction(() => {
            this.isWebSocketHealthy = true;
            this.lastWebSocketActivity = Date.now();
            this.reconnectionAttempts = 0;
            this.isReconnecting = false;
          });

          // Flush any buffered audio data after successful connection
          this.flushAudioBuffer();

          resolve();
        };

        wsConnection.onmessage = event => {
          runInAction(() => (this.lastWebSocketActivity = Date.now()));
          this._handleWebSocketMessage(event);
        };

        wsConnection.onerror = error => {
          clearTimeout(connectionTimeout);
          logger.error({
            message: "[AppStoreAudioCaptureStore] WebSocket Error",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
          runInAction(() => (this.isWebSocketHealthy = false));
          reject(new Error("Failed to connect to transcription service"));
        };

        wsConnection.onclose = event => {
          clearTimeout(connectionTimeout);
          logger.debug({
            message: "[AppStoreAudioCaptureStore] WebSocket Closed",
            info: { code: event.code, reason: event.reason },
          });
          runInAction(() => (this.isWebSocketHealthy = false));
          if (
            this.audioCaptureStore.status === RecordingStatus.RECORDING &&
            !this.isReconnecting &&
            !this.isRefreshingToken
          ) {
            this._handleWebSocketDisconnection(event);
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  async initializeWebSocket(): Promise<void> {
    try {
      if (this._isTokenExpiredOrExpiringSoon()) {
        await this._getAssemblyAiToken();
      }
      await this._createWebSocketConnection();
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Failed to initialize WebSocket, retrying later...",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      this._attemptWebSocketReconnectionAfterDelay();
    }
  }

  _handleWebSocketMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as AssemblyAIMessage;

      // Handle different message types
      switch (data.type) {
        case "Begin":
          logger.debug({
            message: "[AudioCaptureTranscriptionStore] Session started",
            info: {
              sessionId: data.session_id || "unknown",
              expiresAt: data.expires_at || 0,
            },
          });
          break;

        case "Turn":
          if (data.transcript) {
            // Store the turn in our transcript map
            const transcriptKey = data.turn_order + this.transcriptRestoredOffset;
            const timestamp = this.transcriptMap.get(transcriptKey)?.timestamp ?? Date.now();
            this.transcriptMap.set(transcriptKey, {
              ...data,
              turn_order: transcriptKey,
              timestamp,
            });

            this.audioCaptureStore.observability.recordTranscript({
              channel: this.channel,
              wordCount: data.words?.length,
              transcriptLength: data.transcript?.length,
            });

            // Also save to database for persistence
            if (this.audioCaptureStore.activeNoteId) {
              void this.audioCaptureStore.persistence.appendTranscript({
                noteId: this.audioCaptureStore.activeNoteId,
                recordingId: this.audioCaptureStore.currentSession?.recordingId,
                transcript: { ...data, turn_order: transcriptKey, timestamp },
                channel: this.channel,
              });
            }

            // Log turn details for debugging
            logger.debug({
              message: "[AudioCaptureTranscriptionStore] Turn received",
              info: {
                channel: this.channel,
                turnOrder: data.turn_order || 0,
                transcript: data.transcript,
                isFormatted: data.turn_is_formatted || false,
                endOfTurn: data.end_of_turn || false,
                endOfTurnConfidence: data.end_of_turn_confidence || 0,
                wordCount: data.words?.length || 0,
              },
            });
          }
          break;

        case "Termination":
          logger.debug({
            message: "[AudioCaptureTranscriptionStore] Session terminated",
            info: {
              audioDurationSeconds: data.audio_duration_seconds || 0,
              sessionDurationSeconds: data.session_duration_seconds || 0,
            },
          });
          break;

        case "Error":
          logger.error({
            message: "[AudioCaptureTranscriptionStore] AssemblyAI Error",
            info: { error: data.error || "Unknown error" },
          });
          break;
      }
    } catch (error) {
      logger.error({
        message: "[AudioCaptureTranscriptionStore] WebSocket Message Error",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  async cleanupWebSocketAndAudio(): Promise<void> {
    logger.debug({
      message: "[TranscriptionManager] cleanupWebSocketAndAudio",
      info: { channel: this.channel },
    });
    // Send terminate message to AssemblyAI
    if (this.wsConnection?.readyState === WebSocket.OPEN) {
      this.send(JSON.stringify({ terminate_session: true }));
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Close websocket, audioWorkletNode, audioSource, and audioContext
    runInAction(() => {
      if (this.wsConnection) {
        closeWebSocket(this.wsConnection);
        this.wsConnection = null;
      }
      if (this.audioWorkletNode) {
        disconnectAudioNode(this.audioWorkletNode);
        this.audioWorkletNode = null;
      }
      if (this.audioSource) {
        disconnectAudioNode(this.audioSource);
        this.audioSource = null;
      }
    });

    if (this.audioContext) {
      closeAudioContext(this.audioContext);
      runInAction(() => (this.audioContext = null));
    }
  }

  _handleWebSocketDisconnection(closeEvent?: CloseEvent): void {
    if (
      this.isReconnecting ||
      this.isRefreshingToken ||
      this.audioCaptureStore.isRecordingWithoutTranscription
    )
      return;

    logger.warn({
      message: "[AppStoreAudioCaptureStore] WebSocket disconnected, attempting to reconnect",
      info: {
        attempts: this.reconnectionAttempts,
        closeEvent: closeEvent ? { code: closeEvent.code, reason: closeEvent.reason } : null,
      },
    });

    toastModule.triggerToast({
      content: "Connection to transcription service lost. Attempting to reconnect...",
      toastId: this.audioCaptureStore.capturedAudioId,
    });

    this._createTranscriptSnapshot();

    if (closeEvent) {
      // Force reconnection if the app was disconnected.
      this._refreshTokenAndReconnectWebSocket(true);
      return;
    }

    this._attemptWebSocketReconnection();
  }

  _attemptWebSocketReconnectionAfterDelay = debounce(
    () => this._attemptWebSocketReconnection(),
    // 1 second delay between reconnection attempts
    1000,
    { trailing: true, leading: false }
  );

  async _attemptWebSocketReconnection(): Promise<void> {
    this._attemptWebSocketReconnectionAfterDelay.cancel();

    const currentStateExpectsWebSocket = () => {
      return [
        RecordingStatus.INITIALIZING,
        RecordingStatus.INITIALIZED,
        RecordingStatus.RECORDING,
        RecordingStatus.PAUSED,
      ].includes(this.audioCaptureStore.status);
    };

    if (!currentStateExpectsWebSocket()) return;

    if (this.reconnectionAttempts >= this.MAX_RECONNECTION_ATTEMPTS) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Max reconnection attempts reached",
        info: { attempts: this.reconnectionAttempts },
      });
      toastModule.triggerToast({
        content:
          "Unable to connect to transcription service. Your audio is still being recorded, but real-time transcription may be unavailable until the connection is restored.",
        toastId: this.audioCaptureStore.capturedAudioId,
      });
      runInAction(() => {
        this.isReconnecting = false;
        this.audioBuffer = [];
      });
      return;
    }

    runInAction(() => {
      this.isReconnecting = true;
      this.reconnectionAttempts++;
    });

    try {
      await new Promise(resolve => {
        this.reconnectionTimeout = runInAction(() =>
          setTimeout(resolve, this.RECONNECTION_DELAY_MS)
        );
      });

      if (this.wsConnection) {
        closeWebSocket(this.wsConnection);
        runInAction(() => (this.wsConnection = null));
      }

      if (!currentStateExpectsWebSocket()) return;

      if (this._isTokenExpiredOrExpiringSoon()) {
        await this._getAssemblyAiToken();
      }

      await this._createWebSocketConnection();

      logger.debug({
        message: "[AppStoreAudioCaptureStore] WebSocket reconnection successful",
        info: { attempts: this.reconnectionAttempts },
      });
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] WebSocket reconnection failed",
        info: {
          error: error instanceof Error ? error.message : String(error),
          attempts: this.reconnectionAttempts,
        },
      });

      if (this.reconnectionAttempts <= this.MAX_RECONNECTION_ATTEMPTS) {
        runInAction(() => (this.isReconnecting = false));
        this._attemptWebSocketReconnectionAfterDelay();
      }
    }
  }

  /**
   * TOKEN MANAGEMENT
   */

  async _getAssemblyAiToken(): Promise<void> {
    try {
      const response = await api.post("/v2/voice/start_transcription", {
        params: { query: { version: "v3" } },
      });
      if (!response.data?.token) throw new Error("Failed to get AssemblyAI token");
      runInAction(() => {
        this.assemblyAiToken = response.data.token;
        this.assemblyAiTokenExpiresAt = response.data.expires_at
          ? new Date(response.data.expires_at).getTime()
          : null;
        this.assemblyAiTokenSessionEndsAt = Date.now() + this.ASSEMBLY_AI_TOKEN_SESSION_DURATION_MS;
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to get AssemblyAI token";
      logger.error({
        message: "[AudioCaptureTranscriptionStore] Get AssemblyAI Token Error",
        info: { error: errorMessage },
      });
      throw new Error(errorMessage);
    }
  }

  _isTokenExpiredOrExpiringSoon(): boolean {
    if (!this.assemblyAiToken || !this.assemblyAiTokenExpiresAt) {
      return true;
    }

    const now = Date.now();
    const expiresAt = this.assemblyAiTokenExpiresAt;

    return now >= expiresAt - this.TOKEN_EXPIRATION_BUFFER_MS;
  }

  _isSessionExpiredOrExpiringSoon(): boolean {
    if (!this.assemblyAiToken || !this.assemblyAiTokenSessionEndsAt) {
      return true;
    }

    const now = Date.now();
    const expiresAt = this.assemblyAiTokenSessionEndsAt;

    return now >= expiresAt - this.TOKEN_EXPIRATION_BUFFER_MS;
  }

  async _refreshTokenAndReconnectWebSocket(forceRefresh: boolean = false): Promise<void> {
    if (this.isRefreshingToken) return;
    if (!forceRefresh && !this._isTokenExpiredOrExpiringSoon()) return;

    runInAction(() => (this.isRefreshingToken = true));

    logger.debug({
      message:
        "[AppStoreAudioCaptureStore] Refreshing token and reconnecting WebSocket during recording",
      info: {
        currentTime: Date.now(),
        expiresAt: this.assemblyAiTokenExpiresAt,
        wsState: this.wsConnection?.readyState ?? null,
      },
    });

    try {
      await this._getAssemblyAiToken();

      // Only reconnect WebSocket if we're still recording
      if (this.audioCaptureStore.status === RecordingStatus.RECORDING) {
        if (this.wsConnection) {
          this._createTranscriptSnapshot();
          closeWebSocket(this.wsConnection);
          runInAction(() => (this.wsConnection = null));
        }
        await this._createWebSocketConnection();
      }
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Failed to refresh token during recording",
        info: { error: error instanceof Error ? error.message : String(error) },
      });

      toastModule.triggerToast({
        content: "Voice transcription may be interrupted due to connection issues",
        toastId: this.audioCaptureStore.capturedAudioId,
      });
    } finally {
      runInAction(() => (this.isRefreshingToken = false));
    }
  }

  _startTokenMonitoring(): void {
    if (this.tokenCheckInterval) {
      this._stopTokenMonitoring();
    }

    this.tokenCheckInterval = setInterval(async () => {
      try {
        if (this.audioCaptureStore.status === RecordingStatus.RECORDING) {
          await this._refreshTokenAndReconnectWebSocket();
        }
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioCaptureStore] Error in token monitoring interval",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
    }, this.TOKEN_CHECK_INTERVAL_MS);
  }

  _stopTokenMonitoring(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  /**
   * BUFFERING
   */

  _bufferAudioData(audioData: ArrayBuffer): void {
    if (this.isPaused) return;

    this.audioBuffer.push({ data: audioData, timestamp: DateTime.now().toMillis() });
  }

  send(data: Blob | string): void {
    if (this.wsConnection?.readyState !== WebSocket.OPEN) return;
    try {
      this.wsConnection.send(data);
    } catch (error) {
      logger.error({
        message: "[TranscriptionManager] WebSocket send error",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }

  flushAudioBuffer(): void {
    if (this.isPaused) return;
    if (this.audioBuffer.length === 0) return;
    if (!this.wsConnection) return;
    if (this.wsConnection.readyState !== WebSocket.OPEN) return;

    // Each chunk is 8ms
    // We want 50-1000ms, so 7 - 125 chunks
    const minChunks = this.MIN_AUDIO_CHUNKS; // 7 * 8ms = 56ms
    const maxChunks = this.MAX_AUDIO_CHUNKS; // 125 * 8ms = 1000ms

    while (this.audioBuffer.length >= minChunks) {
      const chunksToSend = Math.min(this.audioBuffer.length, maxChunks);
      const bufferedItems = runInAction(() => this.audioBuffer.splice(0, chunksToSend));
      const blob = new Blob(bufferedItems.map(item => item.data));
      this.send(blob);
    }
  }

  _startAudioBufferFlush(): void {
    if (this.audioBufferFlushInterval) {
      this._stopAudioBufferFlush();
    }

    this.audioBufferFlushInterval = setInterval(() => {
      this.flushAudioBuffer();
    }, this.AUDIO_BUFFER_FLUSH_INTERVAL_MS);
  }

  _stopAudioBufferFlush(): void {
    if (this.audioBufferFlushInterval) {
      clearInterval(this.audioBufferFlushInterval);
      this.audioBufferFlushInterval = null;
    }
  }

  /**
   * WEBSOCKET HEALTH CHECKS
   */

  _startWebSocketHealthCheck(): void {
    if (this.webSocketHealthCheckInterval) {
      this._stopWebSocketHealthCheck();
    }

    this.webSocketHealthCheckInterval = setInterval(() => {
      this._checkWebSocketHealth();
    }, this.WEBSOCKET_HEALTH_CHECK_INTERVAL_MS);
  }

  _stopWebSocketHealthCheck(): void {
    if (this.webSocketHealthCheckInterval) {
      clearInterval(this.webSocketHealthCheckInterval);
      this.webSocketHealthCheckInterval = null;
    }
  }

  _checkWebSocketHealth(): void {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastWebSocketActivity;

    // Consider connection unhealthy if no activity for more than 30 seconds during recording
    const isHealthy =
      this.wsConnection?.readyState === WebSocket.OPEN &&
      (this.audioCaptureStore.status !== RecordingStatus.RECORDING ||
        timeSinceLastActivity < this.WEBSOCKET_HEALTH_TIMEOUT_MS);

    if (!isHealthy && this.isWebSocketHealthy) {
      if (this.wsConnection) {
        logger.debug({
          message: "[AudioCaptureTranscriptionStore] WebSocket is silent, pinging it...",
          info: {
            readyState: this.wsConnection?.readyState ?? null,
            timeSinceLastActivity,
            status: this.audioCaptureStore.status,
          },
        });

        // If the websocket is not ok we assume it will fail.
        this.send(JSON.stringify({ type: "UpdateConfiguration" }));
        // Otherwise it should be fine.
        runInAction(() => (this.lastWebSocketActivity = Date.now()));
        return;
      }

      logger.warn({
        message: "[AudioCaptureTranscriptionStore] WebSocket health check failed",
        info: {
          timeSinceLastActivity,
          status: this.audioCaptureStore.status,
        },
      });

      runInAction(() => (this.isWebSocketHealthy = false));

      if (
        this.audioCaptureStore.status === RecordingStatus.RECORDING &&
        !this.isReconnecting &&
        !this.isRefreshingToken
      ) {
        this._handleWebSocketDisconnection();
      }
    } else if (isHealthy && !this.isWebSocketHealthy) {
      runInAction(() => (this.isWebSocketHealthy = true));
    }
  }
}
